
'use client';

import * as React from 'react';
import { Compass, Users, BookOpen, User, Loader2, CalendarDays, Target, Map as MapIcon, X } from 'lucide-react';
import type { PointOfInterest, POIType, Event } from '@/lib/types';
import LocationSidebar from '@/components/location-sidebar';
import { cn, getDistance } from '@/lib/utils';
import CommunityView from '@/components/community-view';
import SunnahView from '@/components/sunnah-view';
import ProfileView from '@/components/profile-view';
import { useAuth } from '@/context/auth-context';
import LoadingAnimation from '@/components/loading-animation';
import AuthGate from '@/components/auth-gate';
import { useToast } from '@/hooks/use-toast';
import EventView from '@/components/event-view';
import HabitTrackerView from '@/components/habit-tracker-view';
import VerifyEmailGate from '@/components/verify-email-gate';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { getPois } from '@/lib/pois';
import { db } from '@/lib/firebase';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, deleteDoc, updateDoc, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore';
import { APIProvider } from '@vis.gl/react-google-maps';
import MapView from './map-view';
import { Button } from './ui/button';

const sydneyCoords = { lat: -33.8688, lng: 151.2093 };
const defaultLocation = sydneyCoords;

type View = (typeof navItemsBase[number]['id'] | typeof navItemsUser[number]['id']);

export default function MainView() {
  const { toast } = useToast();
  const { user, loading, checkPremiumStatus } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();


  // Data fetching and state management
  const [initialPois, setInitialPois] = React.useState<PointOfInterest[]>([]);
  const [pois, setPois] = React.useState<PointOfInterest[]>([]);
  const [posts, setPosts] = React.useState<any[]>([]);
  const [events, setEvents] = React.useState<Event[]>([]);
  const [dataLoading, setDataLoading] = React.useState(true);

  const [mapOpen, setMapOpen] = React.useState(false);

  React.useEffect(() => {
    async function fetchInitialData() {
      try {
        const poisData = await getPois();
        setInitialPois(poisData);
        setPois(poisData);
      } catch (error) {
        console.error("Failed to fetch POIs", error);
        toast({ title: "Error", description: "Could not load location data.", variant: "destructive"});
      } finally {
        setDataLoading(false);
      }
    }
    fetchInitialData();
  }, [toast]);

  // Firestore listener for posts and events
  React.useEffect(() => {
    if (!user) return;
    const postsQuery = query(collection(db, "posts"), orderBy("createdAt", "desc"));
    const eventsQuery = query(collection(db, "events"), orderBy("date", "asc"));

    const unsubPosts = onSnapshot(postsQuery, (querySnapshot) => {
      const postsData: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        postsData.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate().toLocaleString() || new Date().toLocaleString(),
        });
      });
      setPosts(postsData);
    });

    const unsubEvents = onSnapshot(eventsQuery, (querySnapshot) => {
        const eventsData: Event[] = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            eventsData.push({
                id: doc.id,
                ...data,
                date: data.date?.toDate() || new Date(),
                createdAt: data.createdAt?.toDate() || new Date(),
            } as Event);
        });
        setEvents(eventsData);
    });

    return () => {
        unsubPosts();
        unsubEvents();
    };
  }, [user]);


  React.useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if ((pathname === '/success' || pathname === '/cancel') && sessionId) {
      if (pathname === '/success') {
        checkPremiumStatus();
      }
    }
  }, [pathname, searchParams, checkPremiumStatus]);
  
  const [selectedPoi, setSelectedPoi] = React.useState<PointOfInterest | null>(null);
  const [activeFilter, setActiveFilter] = React.useState<POIType | 'all'>('all');
  const [activeView, setActiveView] = React.useState<View>('discover');

  const [searchQuery, setSearchQuery] = React.useState('');
  const [prayerTimesCoords, setPrayerTimesCoords] = React.useState(defaultLocation);
  const [isFindingNearby, setIsFindingNearby] = React.useState(false);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const findNearbyPois = React.useCallback((latitude: number, longitude: number) => {
    try {
      const nearbyPois = initialPois
        .map(poi => ({
          ...poi,
          distance: getDistance(latitude, longitude, poi.lat, poi.lng),
        }))
        .filter(poi => poi.distance <= 50) // 50km radius
        .sort((a, b) => a.distance - b.distance);
      
      setPois(nearbyPois);
      setPrayerTimesCoords({ lat: latitude, lng: longitude });
      setActiveFilter('all');
      setSearchQuery('');

      if (nearbyPois.length === 0) {
        toast({ title: 'No nearby locations found', description: 'There are no listed locations within a 50km radius.' });
        setPois(initialPois); // Show all if none are nearby
      } else {
        toast({ title: 'Nearby locations found!', description: `Showing ${nearbyPois.length} locations sorted by proximity to you.` });
      }
    } catch (error) {
      console.error("Error finding nearby POIs:", error);
      toast({ title: 'Error finding nearby locations', description: 'Could not process nearby locations.', variant: 'destructive' });
      setPois(initialPois); // Fallback to all POIs
    } finally {
      setIsFindingNearby(false);
    }
  }, [initialPois, toast]);

  const handleFindNearby = React.useCallback(() => {
    setIsFindingNearby(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        findNearbyPois(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.error("Geolocation error:", error);
        let title = 'Could not get your location';
        let description = 'An unknown error occurred. Please try again.';

        switch(error.code) {
            case error.PERMISSION_DENIED:
                title = 'Location Access Denied';
                description = 'Please allow location access for this site in your browser settings to use this feature.';
                break;
            case error.POSITION_UNAVAILABLE:
                title = 'Location Unavailable';
                description = 'We were unable to determine your location. Please check your network connection and try again.';
                break;
            case error.TIMEOUT:
                title = 'Location Request Timed Out';
                description = 'It took too long to get your location. Please try again in an area with a better signal.';
                break;
        }

        toast({ title, description, variant: 'destructive' });
        setIsFindingNearby(false);
        setPois(initialPois); // Fallback
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [findNearbyPois, initialPois, toast]);


  // Attempt to find nearby on initial load
  React.useEffect(() => {
    if (initialPois.length > 0) {
      handleFindNearby();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPois.length]); // Only run once when initialPois are loaded


  const filteredPois = React.useMemo(() => {
    let poisToFilter = pois;

    if (activeFilter !== 'all') {
      poisToFilter = poisToFilter.filter((poi) => poi.type === activeFilter);
    }

    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      const searchResults = initialPois.filter(poi => 
        poi.name.toLowerCase().includes(lowerCaseQuery) || 
        (poi.address && poi.address.toLowerCase().includes(lowerCaseQuery))
      );
       return activeFilter !== 'all' ? searchResults.filter(p => p.type === activeFilter) : searchResults;
    }
    
    return poisToFilter;
  }, [activeFilter, searchQuery, pois, initialPois]);


  React.useEffect(() => {
    if (searchQuery.trim() === '') {
        // Don't reset pois if it's already showing nearby results
        return;
    }

    const lowerCaseQuery = searchQuery.toLowerCase();
    const results = initialPois.filter(poi => 
        poi.name.toLowerCase().includes(lowerCaseQuery) || 
        (poi.address && poi.address.toLowerCase().includes(lowerCaseQuery))
      );
    
    setPois(results);

    if (results.length > 0) {
      const firstResult = results[0];
      setPrayerTimesCoords({ lat: firstResult.lat, lng: firstResult.lng });
    }
  }, [searchQuery, initialPois]);


  const handleSelectPoi = React.useCallback((poi: PointOfInterest | null) => {
    setSelectedPoi(poi);
  }, []);

  const handleShowOnMap = React.useCallback((poi: PointOfInterest | null) => {
    setSelectedPoi(poi);
    setMapOpen(true);
  }, []);

  const handleMapPinSelect = React.useCallback((poi: PointOfInterest) => {
    setSelectedPoi(poi);
    setMapOpen(false); // Close map and show details in sidebar
  }, []);

  const getAuthor = () => {
    if (!user) throw new Error("User must be logged in to perform this action.");
    return {
      name: user.displayName || 'Anonymous',
      avatar: user.photoURL || `https://placehold.co/40x40.png?text=${user.displayName?.charAt(0) || 'U'}`,
      aiHint: 'user profile',
      uid: user.uid,
      isOwner: user.isOwner,
      isEarlySupporter: user.isEarlySupporter,
    };
  };

  const handleAddPost = async (post: { title: string; content: string }) => {
    if (!user) return toast({ title: "Please log in to post.", variant: "destructive" });
    try {
        await addDoc(collection(db, "posts"), {
            author: getAuthor(),
            ...post,
            likedBy: [],
            commentsCount: 0,
            createdAt: serverTimestamp(),
        });
    } catch (error) {
        console.error("Error adding post: ", error);
        toast({ title: "Error", description: "Could not create post.", variant: "destructive" });
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!user) return;
    try {
        await deleteDoc(doc(db, "posts", postId));
    } catch (error) {
        console.error("Error deleting post: ", error);
        toast({ title: "Error", description: "Could not delete post.", variant: "destructive" });
    }
  };

   const handleLikePost = async (postId: string) => {
    if (!user) return toast({ title: "Please log in to like posts.", variant: "destructive" });
    const postRef = doc(db, "posts", postId);
    const currentPost = posts.find(p => p.id === postId);
    if (!currentPost) return;

    const alreadyLiked = currentPost.likedBy.includes(user.uid);
    try {
        if (alreadyLiked) {
            await updateDoc(postRef, { likedBy: arrayRemove(user.uid) });
        } else {
            await updateDoc(postRef, { likedBy: arrayUnion(user.uid) });
        }
    } catch (error) {
        console.error("Error liking post: ", error);
        toast({ title: "Error", description: "Could not update like status.", variant: "destructive" });
    }
  };

  const handleAddComment = async (postId: string, text: string) => {
    if (!user) return toast({ title: "Please log in to comment.", variant: "destructive" });
    try {
        const postRef = doc(db, "posts", postId);
        const commentsCol = collection(postRef, "comments");
        await addDoc(commentsCol, {
            author: getAuthor(),
            text,
            createdAt: serverTimestamp(),
        });
    } catch (error) {
        console.error("Error adding comment: ", error);
        toast({ title: "Error", description: "Could not add comment.", variant: "destructive" });
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    if (!user) return;
    try {
        await deleteDoc(doc(db, "posts", postId, "comments", commentId));
    } catch (error) {
        console.error("Error deleting comment: ", error);
        toast({ title: "Error", description: "Could not delete comment.", variant: "destructive" });
    }
  };

  const handleAddEvent = async (event: Omit<Event, 'id' | 'author' | 'createdAt' | 'date'> & { date: string }) => {
    if (!user) return toast({ title: "Please log in to create an event.", variant: "destructive" });

    try {
      const dateString = event.date.replace(/\s/g, '');
      const dateRegex = /^\d{2}-\d{2}-\d{4}$/;

      if (!dateRegex.test(dateString)) {
        throw new Error('Date must be in DD - MM - YYYY format.');
      }

      const [day, month, year] = dateString.split('-').map(Number);

      // Construct date safely to avoid timezone issues.
      // The month is 0-indexed in JavaScript's Date constructor.
      const dateObject = new Date(year, month - 1, day);
      
      if (isNaN(dateObject.getTime()) || dateObject.getDate() !== day || dateObject.getMonth() !== month - 1) {
        throw new Error('The provided date is invalid.');
      }

      const eventData = {
        ...event,
        date: Timestamp.fromDate(dateObject),
        author: getAuthor(),
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "events"), eventData);
      toast({ title: "Event Created!", description: "Your event has been published to the community." });

    } catch (error: any) {
        console.error("Error adding event: ", error);
        toast({
            title: "Error",
            description: error.message || "Could not create event. Please check the details and try again.",
            variant: "destructive"
        });
    }
};

  const handleDeleteEvent = async (eventId: string) => {
    if (!user) return;
    try {
        await deleteDoc(doc(db, "events", eventId));
        toast({ title: "Event Deleted", description: "The event has been removed." });
    } catch (error) {
        console.error("Error deleting event: ", error);
        toast({ title: "Error", description: "Could not delete event.", variant: "destructive" });
    }
  };

  if (loading || dataLoading) {
      return <LoadingAnimation />;
  }

  if (!user) {
    return <AuthGate />;
  }

  if (!user.emailVerified) {
    return <VerifyEmailGate />;
  }

  const discoverView = (
    <LocationSidebar
        pois={filteredPois}
        selectedPoi={selectedPoi}
        onSelectPoi={handleSelectPoi}
        onShowOnMap={handleShowOnMap}
        onClearSelection={() => handleSelectPoi(null)}
        activeFilter={activeFilter}
        onSetFilter={setActiveFilter}
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchChange}
        prayerTimesCoords={prayerTimesCoords}
        onFindNearby={handleFindNearby}
        isFindingNearby={isFindingNearby}
    />
  );

  const renderActiveView = () => {
    switch (activeView) {
        case 'discover':
            return discoverView;
        case 'community':
            return (
                <CommunityView 
                    posts={posts} 
                    onAddPost={handleAddPost} 
                    onDeletePost={handleDeletePost} 
                    onLikePost={handleLikePost}
                    onAddComment={handleAddComment}
                    onDeleteComment={handleDeleteComment}
                />
            );
        case 'sunnah':
            return <SunnahView />;
        case 'events':
            return <EventView events={events} onAddEvent={handleAddEvent} onDeleteEvent={handleDeleteEvent} />;
        case 'habits':
             return user ? <HabitTrackerView /> : <AuthGate />;
        case 'profile':
            return user ? <ProfileView /> : <AuthGate />;
        default:
            return null; // Should not happen
    }
  }

  return (
     <div className="h-screen w-screen flex flex-col">
      <main className="flex-1 flex flex-col overflow-auto no-scrollbar">
        {renderActiveView()}
      </main>

      {mapOpen && (
          <APIProvider apiKey={process.env.NEXT_PUBLIC_MAPS_API_KEY || ''}>
              <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
                  <div className="absolute inset-0">
                      <MapView
                          pois={filteredPois}
                          selectedPoi={selectedPoi}
                          onSelectPoi={handleMapPinSelect}
                      />
                  </div>
                  <Button 
                      variant="ghost" 
                      size="icon" 
                      className="absolute top-4 right-4 z-50 bg-background rounded-full shadow-lg hover:bg-muted"
                      onClick={() => setMapOpen(false)}
                    >
                      <X className="w-5 h-5"/>
                  </Button>
              </div>
          </APIProvider>
      )}

      <BottomNavBar activeView={activeView} onNavigate={setActiveView} user={user} onMapClick={() => handleShowOnMap(null)} />
    </div>
  );
}

const navItemsBase = [
  { id: 'discover', label: 'Discover', icon: Compass },
  { id: 'community', label: 'Community', icon: Users },
  { id: 'sunnah', label: 'Sunnah', icon: BookOpen },
  { id: 'events', label: 'Events', icon: CalendarDays },
] as const;

const navItemsUser = [
  { id: 'habits', label: 'Habits', icon: Target },
  { id: 'profile', label: 'Profile', icon: User },
] as const;


function BottomNavBar({ activeView, onNavigate, user, onMapClick }: { activeView: View; onNavigate: (view: View) => void, user: any, onMapClick: () => void }) {
  const navItems = [...navItemsBase];

  // Insert Map button in the middle
  const middleIndex = Math.ceil(navItems.length / 2);
  navItems.splice(middleIndex, 0, { id: 'map', label: 'Map', icon: MapIcon } as any);

  const allNavItems = [...navItems, ...navItemsUser];
  
  return (
    <nav className="flex justify-around items-center p-2 bg-background border-t">
      {allNavItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeView === item.id;
        
        if (!user && (item.id === 'profile' || item.id === 'habits')) {
          return null;
        }

        const handleClick = () => {
            if (item.id === 'map') {
                onMapClick();
            } else {
                onNavigate(item.id as View);
            }
        }

        return (
          <button
            key={item.id}
            onClick={handleClick}
            className={cn(
              "flex flex-col items-center gap-1 p-2 rounded-lg transition-colors w-16",
              isActive && item.id !== 'map' ? "text-primary" : "text-muted-foreground"
            )}
          >
            <Icon className="w-6 h-6" />
            <span className="text-xs font-medium">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
